
import { useState, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Button,
  Select,
  Grid,
  Spinner,
  Box,
  Heading,
  Card,
  CardBody,
  Badge,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Globe, Clock, CheckCircle2 } from 'lucide-react'
import { SettingsService, GeneralSettingsData } from '@/lib/services/settingsService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { timezones, dateFormats, timeFormats } from '@/lib/utils/dateTimeUtils'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface GeneralSettingsFormProps {
  onSuccess?: () => void
}

export function GeneralSettingsForm({ onSuccess }: GeneralSettingsFormProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<GeneralSettingsData | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const { showError, showSuccess } = useToastNotification()
  const { refresh: refreshSiteSettings } = useSiteSettings()

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      console.log('[GeneralSettingsForm] Loading settings...')
      const response = await SettingsService.getGeneralSettings()
      console.log('[GeneralSettingsForm] Settings loaded:', response)

      if (response.success && response.data) {
        // Ensure all required fields exist with proper defaults
        const loadedSettings: GeneralSettingsData = {
          site: {},
          date_time: {
            timezone: response.data.date_time?.timezone ?? 'UTC',
            date_format: response.data.date_time?.date_format ?? 'Y-m-d',
            time_format: response.data.date_time?.time_format ?? 'H:i:s',
          },
          language: {
            default_language: response.data.language?.default_language ?? 'en',
          },
        }
        setSettings(loadedSettings)
        setHasChanges(false)
      } else {
        // Initialize with defaults if no data
        const defaultSettings: GeneralSettingsData = {
          site: {},
          date_time: {
            timezone: 'UTC',
            date_format: 'Y-m-d',
            time_format: 'H:i:s',
          },
          language: {
            default_language: 'en',
          },
        }
        setSettings(defaultSettings)
        setHasChanges(false)
      }
    } catch (err: any) {
      // Initialize with defaults on error
      console.error('[GeneralSettingsForm] Error loading settings:', err)
      console.error('[GeneralSettingsForm] Error response:', err.response)
      const defaultSettings: GeneralSettingsData = {
        site: {},
        date_time: {
          timezone: 'UTC',
          date_format: 'Y-m-d',
          time_format: 'H:i:s',
        },
        language: {
          default_language: 'en',
        },
      }
      setSettings(defaultSettings)
      setHasChanges(false)
      showError({ message: err.message || 'Failed to load general settings' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!settings) return

    try {
      setSaving(true)
      setErrors({}) // Clear previous errors
      // Scroll to top before saving
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const response = await SettingsService.updateGeneralSettings(settings)
      // Check if response indicates success
      if (response && (response.success === true || (response.message && response.success !== false))) {
        showSuccess('Success', response.message || 'General settings updated successfully')
        // Reload settings to ensure form displays latest data
        await loadSettings()
        // Refresh site settings cache so all components using useSiteSettings get updated values
        refreshSiteSettings()
        setHasChanges(false)
        setLastSaved(new Date())
        setErrors({}) // Clear errors on success
        // Scroll to top after successful save
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
        onSuccess?.()
      } else {
        throw new Error(response.message || 'Failed to update general settings')
      }
    } catch (err: any) {
      // Handle validation errors
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors
        setErrors(validationErrors)

        // Show general error message
        const errorMessages: string[] = []
        Object.entries(validationErrors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            errorMessages.push(...messages)
          }
        })

        if (errorMessages.length > 0) {
          showError({ message: errorMessages.join(', ') })
        } else {
          showError({ message: err.response.data.message || 'Validation failed' })
        }
      } else {
        // Handle other errors
        setErrors({})
        showError(err)
      }
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (section: keyof GeneralSettingsData, key: string, value: any) => {
    if (!settings) return

    setHasChanges(true)
    setSettings((prev) => {
      if (!prev) return null
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value,
        },
      }
    })
  }


  // Date/time utilities are imported from @/lib/utils/dateTimeUtils

  const colors = useThemeColors()
  const cardBg = colors.cardBg
  const borderColor = colors.border

  if (loading || !settings) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading general settings...</Text>
      </VStack>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Display general validation errors */}
      {Object.keys(errors).length > 0 && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="semibold" mb={2}>Please fix the following errors:</Text>
            <VStack align="stretch" spacing={1}>
              {Object.entries(errors).map(([field, messages]) => (
                <Box key={field}>
                  {Array.isArray(messages) && messages.map((msg, idx) => (
                    <Text key={idx} fontSize="sm">• {msg}</Text>
                  ))}
                </Box>
              ))}
            </VStack>
          </AlertDescription>
        </Alert>
      )}

      {/* Date & Time Settings Section */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="orange.100" borderRadius="md" color="orange.600">
              <Clock size={20} />
            </Box>
            <Heading size="sm" fontWeight="semibold">
              Date & Time Settings
            </Heading>
          </HStack>

          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Timezone
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.timezone || 'UTC'}
                    onChange={(e) => updateSetting('date_time', 'timezone', e.target.value)}
                    flex="1"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                    {settings?.date_time?.timezone || 'UTC'}
                  </Badge>
                </HStack>
                <FormHelperText>Default timezone for the application</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Date Format
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.date_format || 'Y-m-d'}
                    onChange={(e) => updateSetting('date_time', 'date_format', e.target.value)}
                    flex="1"
                  >
                    {dateFormats.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </Select>
                  <Tooltip label="Preview of current date format">
                    <Badge colorScheme="purple" fontSize="sm" px={2} py={1} cursor="help">
                      {(() => {
                        const now = new Date()
                        const format = settings?.date_time?.date_format || 'Y-m-d'
                        if (format === 'Y-m-d') return now.toISOString().split('T')[0]
                        if (format === 'm/d/Y') return `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`
                        if (format === 'd/m/Y') return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`
                        return now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                      })()}
                    </Badge>
                  </Tooltip>
                </HStack>
                <FormHelperText>How dates are displayed throughout the application</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Time Format
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.time_format || 'H:i:s'}
                    onChange={(e) => updateSetting('date_time', 'time_format', e.target.value)}
                    flex="1"
                  >
                    {timeFormats.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                    {new Date().toLocaleTimeString('en-US', {
                      hour12: settings?.date_time?.time_format?.includes('A') || false
                    })}
                  </Badge>
                </HStack>
                <FormHelperText>How times are displayed throughout the application</FormHelperText>
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={4} justify="space-between" align="center">
            <HStack spacing={2}>
              {hasChanges && (
                <Badge colorScheme="orange" fontSize="xs">
                  Unsaved changes
                </Badge>
              )}
              {lastSaved && !hasChanges && (
                <HStack spacing={1} color="green.500">
                  <CheckCircle2 size={14} />
                  <Text fontSize="xs" color="green.500">
                    Saved {lastSaved.toLocaleTimeString()}
                  </Text>
                </HStack>
              )}
            </HStack>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              size="md"
              px={8}
              isDisabled={!hasChanges && !saving}
            >
              Save Settings
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  )
}

